import { useEffect, useRef, useState, useCallback } from 'react';

// Inactivity limit before showing modal (10 minutes in ms)
const INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 minutes
// Countdown duration for modal (5 minutes in seconds)
const COUNTDOWN_START = 5 * 60; // 5 minutes
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp'; // Key for sessionStorage
const INACTIVITY_LOGOUT_TIMESTAMP_KEY = 'inactivityLogoutTimestamp'; // Key for sessionStorage - timestamp when user was logged out due to inactivity
const RETURN_URL_KEY = 'hxReturnUrl'; // Key for sessionStorage - URL to redirect to after login
const SYSTEM_SESSION_ID_KEY = 'systemSessionId';
const SYSTEM_LOGOUT_REASON_KEY = 'systemLogoutReason';

const notifyInactivityLogout = () => {
  const sessionId = sessionStorage.getItem(SYSTEM_SESSION_ID_KEY);
  const accessToken = sessionStorage.getItem('accessToken');
  if (!sessionId || !accessToken) {
    return;
  }

  const apiBaseUrl = import.meta.env.VITE_APP_API_URL || window.location.origin;
  fetch(`${apiBaseUrl}/auth/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      sessionId: sessionId,
      logoutType: 'InactivityAutoLogout',
    }),
    keepalive: true,
  }).catch(() => {
    // Best-effort call only.
  });
};

const clearSessionForInactivity = () => {
  sessionStorage.setItem(SYSTEM_LOGOUT_REASON_KEY, 'InactivityAutoLogout');
  notifyInactivityLogout();
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('userInfo');
  sessionStorage.removeItem('userRoles');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('lastClickedElement');
  sessionStorage.removeItem('lastActivityTimestamp');
  // Clear compliance flags so they don't carry forward into the next login.
  sessionStorage.removeItem('hasAcceptedCompliance');
  sessionStorage.removeItem('triggerCompliance');
  sessionStorage.removeItem('triggerComplianceOnLegalPage');
  sessionStorage.removeItem('isFirstTimeLegalFlow');
};

/**
 * Utility function to check if user should be logged out
 * This runs synchronously and can be called before rendering
 * @returns {boolean} True if user should be logged out
 */
export function checkShouldLogout() {
  // Check if user is logged in
  const accessToken = sessionStorage.getItem('accessToken');
  if (!accessToken) {
    return false; // Not logged in, no need to logout
  }

  const currentTime = Date.now();
  const storedLastActivity = parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
  
  // If no stored activity, user just logged in
  if (!storedLastActivity) {
    return false;
  }

  const timeSinceLastActivity = currentTime - storedLastActivity;
  
  // If total allowed time (inactivity + countdown) has passed, should logout
  return timeSinceLastActivity >= INACTIVITY_LIMIT + COUNTDOWN_START * 1000;
}

/**
 * Custom hook to handle user inactivity and automatic logout
 * @param {Object} options - Configuration options
 * @param {Function} options.onInactivity - Callback when user becomes inactive
 * @param {Function} options.onCountdownEnd - Callback when countdown ends
 * @param {boolean} options.shouldTrackInactivity - Whether to track inactivity on current route
 * @returns {Object} Timer state and control functions
 */
export function useInactivityTimer({ onInactivity, onCountdownEnd, shouldTrackInactivity = true }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_START);
  const [isCheckingInactivity, setIsCheckingInactivity] = useState(false); // Only true when returning from inactive tab
  const inactivityTimeout = useRef(null);
  const countdownInterval = useRef(null);
  const lastActivityTime = useRef(Date.now());
  const isPageVisible = useRef(true);
  const animationFrameId = useRef(null);
  const lastCheckTime = useRef(Date.now());
  const hasLoggedOut = useRef(false); // Track if logout has already occurred
  const hasInitialized = useRef(false); // Track if hook has been initialized
  // Captures the countdown value in effect at the moment setModalOpen(true) is called so the
  // countdown effect's hidden-tab branch can count down from the accurate remaining time
  // (not always from COUNTDOWN_START).
  const modalOpenCountdownRef = useRef(COUNTDOWN_START);

  // Keep latest callback props in refs so effects never re-run just because the
  // consumer passed a new arrow-function reference. Without this, every App.jsx
  // re-render resets the 5-minute countdown because onCountdownEnd is in the
  // countdown effect's dependency array and gets a new identity each render.
  const onCountdownEndRef = useRef(onCountdownEnd);
  onCountdownEndRef.current = onCountdownEnd;
  const onInactivityRef = useRef(onInactivity);
  onInactivityRef.current = onInactivity;

  /**
   * Check if user is currently logged in by looking for accessToken in sessionStorage
   * @returns {boolean} True if user is logged in, false otherwise
   */
  const isUserLoggedIn = useCallback(() => {
    return !!sessionStorage.getItem('accessToken');
  }, []);

  /**
   * Clean up all timers and reset state when user is not logged in
   */
  const cleanupOnLogout = useCallback(() => {
    setModalOpen(false);
    setCountdown(COUNTDOWN_START);
    hasLoggedOut.current = false;
    
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
      inactivityTimeout.current = null;
    }
    
    if (countdownInterval.current) {
      cancelAnimationFrame(countdownInterval.current);
      countdownInterval.current = null;
    }
    
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
  }, []);

  /**
   * Update last activity in sessionStorage and ref
   */
  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    lastActivityTime.current = now;
    sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  }, []);

  /**
   * Check if user should be logged out based on time difference
   * Triggers logout if total allowed time has passed
   */
  const checkInactivity = useCallback(() => {
    // Only check inactivity if user is logged in and tracking is enabled
    if (!isUserLoggedIn() || !shouldTrackInactivity) {
      if (!isUserLoggedIn()) {
        cleanupOnLogout();
      }
      return false;
    }

    const currentTime = Date.now();
    // Try to get last activity from sessionStorage for cross-tab and minimize support
    const storedLastActivity = parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
    const lastActive = Math.max(lastActivityTime.current, storedLastActivity);
    const timeSinceLastActivity = currentTime - lastActive;
    
    // If total allowed time (inactivity + countdown) has passed, logout immediately
    if (timeSinceLastActivity >= INACTIVITY_LIMIT + COUNTDOWN_START * 1000) {
      if (!hasLoggedOut.current) {
        hasLoggedOut.current = true;
        // closing model
        setModalOpen(false);
        
        // Save current URL and logout timestamp before clearing sessionStorage
        const currentUrl = window.location.pathname + window.location.search + window.location.hash;
        const logoutTimestamp = Date.now();
        sessionStorage.setItem(INACTIVITY_LOGOUT_TIMESTAMP_KEY, logoutTimestamp.toString());
        sessionStorage.setItem(RETURN_URL_KEY, currentUrl);
        
        // Clear sessionStorage before redirect to ensure clean logout
        clearSessionForInactivity();
        
        // Only redirect immediately if not coming from visibility change (no loading overlay)
        // If coming from visibility change, the loading overlay is handled there
        if (!isCheckingInactivity) {
          // Redirect to login with return URL in query params
          const encodedReturnUrl = encodeURIComponent(currentUrl);
          window.location.href = `/login?reason=inactivity&hxReturnUrl=${encodedReturnUrl}`;
        }
        onCountdownEndRef.current?.();
      }
      return true;
    }

    // Only show modal if user is actively on the page (not during loading/checking from visibility change,
    // and not while the tab is hidden). If the page is hidden, the visibilitychange handler will show
    // the modal with an accurate remaining countdown when the user returns, avoiding a stale full-countdown
    // flash from a resumed rAF loop.
    if (timeSinceLastActivity >= INACTIVITY_LIMIT && !modalOpen && !isCheckingInactivity && isPageVisible.current) {
      modalOpenCountdownRef.current = COUNTDOWN_START;
      setModalOpen(true);
      setCountdown(COUNTDOWN_START);
      onInactivityRef.current?.();
    }
    return false;
  }, [modalOpen, isUserLoggedIn, cleanupOnLogout, shouldTrackInactivity, isCheckingInactivity]);

  /**
   * Reset inactivity timer
   */
  const resetInactivity = useCallback(() => {
    // Only reset timer if user is logged in and tracking is enabled
    if (!isUserLoggedIn() || !shouldTrackInactivity) {
      if (!isUserLoggedIn()) {
        cleanupOnLogout();
      }
      return;
    }
    
    updateLastActivity();
    if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
    if (modalOpen) return; // Don't reset if modal is open
    
    inactivityTimeout.current = setTimeout(() => {
      checkInactivity();
    }, INACTIVITY_LIMIT);
  }, [modalOpen, checkInactivity, updateLastActivity, isUserLoggedIn, cleanupOnLogout, shouldTrackInactivity]);

  /**
   * Reset everything (used for Continue button)
   */
  const resetAll = useCallback(() => {
    // Only reset if user is logged in and tracking is enabled
    if (!isUserLoggedIn() || !shouldTrackInactivity) {
      if (!isUserLoggedIn()) {
        cleanupOnLogout();
      }
      return;
    }
    
    setModalOpen(false);
    setCountdown(COUNTDOWN_START);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    hasLoggedOut.current = false;
    resetInactivity();
  }, [resetInactivity, isUserLoggedIn, cleanupOnLogout, shouldTrackInactivity]);

  /**
   * Handle visibility change (tab switch/minimize/restore)
   * If returning to tab and time has expired, trigger logout immediately
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasHidden = !isPageVisible.current;
      isPageVisible.current = document.visibilityState === 'visible';
      
      if (isPageVisible.current && wasHidden && hasInitialized.current) {
        // Only check if user is logged in and tracking is enabled
        if (!isUserLoggedIn() || !shouldTrackInactivity) {
          if (!isUserLoggedIn()) {
            cleanupOnLogout();
          }
          return;
        }
        
        // STEP 1: Calculate elapsed time and decide what to do
        const currentTime = Date.now();
        const storedLastActivity = parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
        const lastActive = Math.max(lastActivityTime.current, storedLastActivity);
        const timeSinceLastActivity = currentTime - lastActive;

        let action = 'continue';

        if (timeSinceLastActivity >= INACTIVITY_LIMIT + COUNTDOWN_START * 1000) {
          action = 'logout';
        } else if (timeSinceLastActivity >= INACTIVITY_LIMIT) {
          action = 'showModal';
        }

        // Brief tab switches that need no action: skip the overlay entirely to
        // avoid a jarring 1-second "Checking session..." flash (common on Mac
        // when Cmd+Tab switching or minimising briefly).
        if (action === 'continue') {
          return;
        }

        // STEP 2: Show loading overlay — only for logout / show-modal cases
        setIsCheckingInactivity(true);

        // STEP 3: 1-second transition buffer, then execute action.
        // Recalculate inside the callback so the elapsed second is accounted for —
        // the initial snapshot above may have been right at the showModal/logout
        // boundary, and one more second may have pushed it over.
        setTimeout(() => {
          const elapsedNow = Date.now() - lastActive;
          const hasFullyExpired = elapsedNow >= INACTIVITY_LIMIT + COUNTDOWN_START * 1000;

          if (action === 'logout' || hasFullyExpired) {
            if (!hasLoggedOut.current) {
              hasLoggedOut.current = true;

              const currentUrl = window.location.pathname + window.location.search + window.location.hash;
              sessionStorage.setItem(INACTIVITY_LOGOUT_TIMESTAMP_KEY, Date.now().toString());
              sessionStorage.setItem(RETURN_URL_KEY, currentUrl);

              clearSessionForInactivity();

              const encodedReturnUrl = encodeURIComponent(currentUrl);
              window.location.href = `/login?reason=inactivity&hxReturnUrl=${encodedReturnUrl}`;
              onCountdownEndRef.current?.();
            }
          } else {
            // Recalculate remaining countdown using fresh elapsed time so the
            // displayed value is accurate after the 1-second overlay delay.
            const timeInCountdownNow = elapsedNow - INACTIVITY_LIMIT;
            const accurateRemaining = Math.max(1, COUNTDOWN_START - Math.floor(timeInCountdownNow / 1000));

            modalOpenCountdownRef.current = accurateRemaining;
            setIsCheckingInactivity(false);
            setModalOpen(true);
            setCountdown(accurateRemaining);
            onInactivityRef.current?.();
          }
        }, 1000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isUserLoggedIn, cleanupOnLogout, shouldTrackInactivity]);

  /**
   * Handle changes to shouldTrackInactivity - stop tracking when disabled
   */
  useEffect(() => {
    if (!shouldTrackInactivity) {
      // If tracking is disabled, clean up all timers and close modal
      setModalOpen(false);
      setCountdown(COUNTDOWN_START);
      hasLoggedOut.current = false;
      
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
        inactivityTimeout.current = null;
      }
      
      if (countdownInterval.current) {
        cancelAnimationFrame(countdownInterval.current);
        countdownInterval.current = null;
      }
      
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    }
  }, [shouldTrackInactivity]);

  /**
   * On mount, check if user should be logged out (handles reload after being away)
   * Only show loading overlay if we're returning from an inactive state
   */
  useEffect(() => {
    // Only check inactivity on mount if user is logged in and tracking is enabled
    if (isUserLoggedIn() && shouldTrackInactivity) {
      checkInactivity();
    } else if (!isUserLoggedIn()) {
      // User is not logged in, clean up any existing state
      cleanupOnLogout();
    }
    
    hasInitialized.current = true;
  }, [shouldTrackInactivity, isUserLoggedIn, checkInactivity, cleanupOnLogout]);

  /**
   * Set up a continuous check using requestAnimationFrame
   * This is throttled/paused by browsers when tab is inactive
   */
  useEffect(() => {
    const checkInactivityFrame = () => {
      // Only continue checking if user is logged in and tracking is enabled
      if (!isUserLoggedIn() || !shouldTrackInactivity) {
        if (!isUserLoggedIn()) {
          // Clean up and stop checking if user is not logged in
          cleanupOnLogout();
        }
        return;
      }

      const currentTime = Date.now();
      const storedLastActivity = parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      const lastActive = Math.max(lastActivityTime.current, storedLastActivity);
      const timeSinceLastActivity = currentTime - lastActive;
      
      // Only log if more than 1 second has passed since last check
      if (currentTime - lastCheckTime.current >= 1000) {
        lastCheckTime.current = currentTime;
      }
      
      if (timeSinceLastActivity >= INACTIVITY_LIMIT + COUNTDOWN_START * 1000) {
        if (!hasLoggedOut.current) {
          hasLoggedOut.current = true;
          // Close modal immediately when logout is triggered
          setModalOpen(false);
          
          // Save current URL and logout timestamp before clearing sessionStorage
          const currentUrl = window.location.pathname + window.location.search + window.location.hash;
          const logoutTimestamp = Date.now();
          sessionStorage.setItem(INACTIVITY_LOGOUT_TIMESTAMP_KEY, logoutTimestamp.toString());
          sessionStorage.setItem(RETURN_URL_KEY, currentUrl);
          
          // Clear sessionStorage before redirect to ensure clean logout
          clearSessionForInactivity();
          
          // Redirect to login with return URL in query params
          const encodedReturnUrl = encodeURIComponent(currentUrl);
          window.location.href = `/login?reason=inactivity&hxReturnUrl=${encodedReturnUrl}`;
          onCountdownEndRef.current?.();
        }
        return;
      }

      // Schedule next check
      animationFrameId.current = requestAnimationFrame(checkInactivityFrame);
    };

    // Start the continuous check
    animationFrameId.current = requestAnimationFrame(checkInactivityFrame);
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [isUserLoggedIn, cleanupOnLogout, shouldTrackInactivity]);

  /**
   * Countdown effect for modal, uses requestAnimationFrame
   * Handles both visible and hidden tab scenarios
   */
  useEffect(() => {
    if (modalOpen && isUserLoggedIn() && shouldTrackInactivity) {
      // Use the ref set just before setModalOpen(true) so we have the accurate
      // starting value (e.g. accurateRemaining from visibilitychange) without
      // putting `countdown` state in the dep array (which would restart the effect
      // on every tick and break the countdown logic).
      const startCountdown = modalOpenCountdownRef.current;
      const startTime = Date.now();
      let lastUpdateTime = startTime;
      const updateCountdown = () => {
        // Check if user is still logged in and tracking is enabled
        if (!isUserLoggedIn() || !shouldTrackInactivity) {
          if (!isUserLoggedIn()) {
            cleanupOnLogout();
          }
          return;
        }

        const currentTime = Date.now();
        const timeDiff = Math.floor((currentTime - startTime) / 1000);
        if (!isPageVisible.current) {
          // If tab is hidden, calculate countdown based on elapsed time from the
          // actual starting value (not always COUNTDOWN_START).
          const newCountdown = Math.max(0, startCountdown - timeDiff);
          if (newCountdown <= 0) {
            if (!hasLoggedOut.current) {
              hasLoggedOut.current = true;
              // Close modal immediately when logout is triggered
              setModalOpen(false);
              
              // Save current URL and logout timestamp before clearing sessionStorage
              const currentUrl = window.location.pathname + window.location.search + window.location.hash;
              const logoutTimestamp = Date.now();
              sessionStorage.setItem(INACTIVITY_LOGOUT_TIMESTAMP_KEY, logoutTimestamp.toString());
              sessionStorage.setItem(RETURN_URL_KEY, currentUrl);
              
              // Clear sessionStorage before redirect to ensure clean logout
              clearSessionForInactivity();
              
              // Redirect to login with return URL in query params
              const encodedReturnUrl = encodeURIComponent(currentUrl);
              window.location.href = `/login?reason=inactivity&hxReturnUrl=${encodedReturnUrl}`;
              onCountdownEndRef.current?.();
            }
            return;
          }
          setCountdown(newCountdown);
        } else {
          // Only update if at least 1 second has passed
          if (currentTime - lastUpdateTime >= 1000) {
            setCountdown((prev) => {
              if (prev <= 1) {
                if (!hasLoggedOut.current) {
                  hasLoggedOut.current = true;
                  // Close modal immediately when logout is triggered
                  setModalOpen(false);

                  // Save current URL and logout timestamp before clearing sessionStorage
                  const currentUrl = window.location.pathname + window.location.search + window.location.hash;
                  const logoutTimestamp = Date.now();
                  sessionStorage.setItem(INACTIVITY_LOGOUT_TIMESTAMP_KEY, logoutTimestamp.toString());
                  sessionStorage.setItem(RETURN_URL_KEY, currentUrl);

                  // Clear sessionStorage before redirect to ensure clean logout
                  clearSessionForInactivity();

                  // Redirect to login with return URL in query params
                  const encodedReturnUrl = encodeURIComponent(currentUrl);
                  window.location.href = `/login?reason=inactivity&hxReturnUrl=${encodedReturnUrl}`;
                  onCountdownEndRef.current?.();
                }
                return 0;
              }
              lastUpdateTime = currentTime;
              return prev - 1;
            });
          }
        }
        countdownInterval.current = requestAnimationFrame(updateCountdown);
      };
      countdownInterval.current = requestAnimationFrame(updateCountdown);
    } else if (!isUserLoggedIn()) {
      // User is not logged in, clean up
      cleanupOnLogout();
    }
    return () => {
      if (countdownInterval.current) {
        cancelAnimationFrame(countdownInterval.current);
      }
    };
  }, [modalOpen, isUserLoggedIn, cleanupOnLogout, shouldTrackInactivity]);

  /**
   * Listen for user activity and reset inactivity timer
   */
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    const activityHandler = () => {
      // Only reset timer if user is logged in and tracking is enabled
      if (isUserLoggedIn() && shouldTrackInactivity) {
        resetInactivity();
      }
    };
    
    events.forEach((event) => window.addEventListener(event, activityHandler));
    
    // On mount, initialize last activity from sessionStorage if available and user is logged in
    if (isUserLoggedIn() && shouldTrackInactivity) {
      const storedLastActivity = parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      if (storedLastActivity) {
        lastActivityTime.current = storedLastActivity;
      } else {
        updateLastActivity();
      }
      resetInactivity();
    } else if (!isUserLoggedIn()) {
      // User is not logged in, clean up
      cleanupOnLogout();
    }
    
    return () => {
      events.forEach((event) => window.removeEventListener(event, activityHandler));
      if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [resetInactivity, updateLastActivity, isUserLoggedIn, cleanupOnLogout, shouldTrackInactivity]);

  // Return timer state and control functions
  return {
    modalOpen,
    countdown,
    resetAll,
    setModalOpen,
    isCheckingInactivity, // New state to show loading during checks
  };
}
